import axios, { AxiosInstance } from 'axios';
import { loginRequest } from '../authConfig';
import { getRuntimeEnv } from '@utils/runtimeEnv';

interface StreamDataParams {
  startIndex?: string;
  endIndex?: string;
  count?: number;
}
interface StreamParams {
  skip?: number;
  count?: number;
  query?: string;
}
class AvevaApiService {
  private readonly apiClient: AxiosInstance;
  private msalInstance: any;
  private readonly baseURL: string;
  constructor() {
    const host = getRuntimeEnv('REACT_APP_SERVER_HOST');

    this.baseURL = host === 'localhost:4000'
      ? 'http://localhost:4000'
      : `https://${host}`;
    this.apiClient = axios.create({
      baseURL: `${this.baseURL}/api/aveva`,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  setMsalInstance(msalInstance: any) {
    this.msalInstance = msalInstance;
  }

  setupInterceptors() {
    // Request interceptor to add MSAL token
    this.apiClient.interceptors.request.use(
      async (config) => {
        if (this.msalInstance) {
          try {
            const account = this.msalInstance.getActiveAccount();
            if (account) {
              const tokenResponse = await this.msalInstance.acquireTokenSilent({
                ...loginRequest,
                account: account,
              });

              if (tokenResponse.accessToken) {
                config.headers.Authorization = `Bearer ${tokenResponse.accessToken}`;
              }
            }
          } catch (error) {
            console.warn('Failed to acquire token for AVEVA API:', error);
            // Continue without token for public endpoints
          }
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor for error handling
    this.apiClient.interceptors.response.use(
      (response) => response.data,
      async (error) => {
        console.error('AVEVA API Error:', error.response?.data || error.message);

        // Handle 401 errors
        if (error.response?.status === 401 && this.msalInstance) {
          try {
            const account = this.msalInstance.getActiveAccount();
            if (account) {
              // Try to refresh token
              await this.msalInstance.acquireTokenSilent({
                ...loginRequest,
                account: account,
                forceRefresh: true,
              });

              // Retry the original request once
              if (!error.config._retry) {
                error.config._retry = true;
                return this.apiClient.request(error.config);
              }
            }
          } catch (tokenError) {
            console.error('Token refresh failed:', tokenError);
          }
        }

        throw error.response?.data || error;
      }
    );
  }

  // Public endpoints (no auth required)
  async checkHealth() {
    return await this.apiClient.get('/health');
  }

  async testAvevaCredentials() {
    return await this.apiClient.get('/test-aveva-creds');
  }

  // Protected endpoints (require auth)
  async testConnection() {
    return await this.apiClient.get('/test-connection');
  }

  async getStreams(params: StreamParams = {}) {
    const { skip = 0, count = 100, query } = params;
    const queryParams = new URLSearchParams({ skip: skip.toString(), count: count.toString() });

    if (query) queryParams.append('query', query);
    return await this.apiClient.get(`/streams?${queryParams}`);
  }

  async searchStreams(query: string) {
    return await this.apiClient.get(`/streams/search?q=${encodeURIComponent(query)}`);
  }

  async getStream(streamId: string) {
    return await this.apiClient.get(`/streams/${streamId}`);
  }

  async getStreamData(streamId: string, params: StreamDataParams = {}) {
    const { startIndex, endIndex, count } = params;
    const queryParams = new URLSearchParams();

    if (startIndex) queryParams.append('startIndex', startIndex);
    if (endIndex) queryParams.append('endIndex', endIndex);
    if (count) queryParams.append('count', count.toString());

    const query = queryParams.toString() ? `?${queryParams}` : '';
    return await this.apiClient.get(`/streams/${streamId}/data${query}`);
  }

  async getLatestValue(streamId: string) {
    return await this.apiClient.get(`/streams/${streamId}/latest`);
  }

  async getBulkStreamsData(streamIds: string[], latest = true) {
    return await this.apiClient.post('/streams/bulk', {
      streamIds,
      latest,
    });
  }

  async getMeterMaxCap(meterStreamId: string) {
    return await this.apiClient.get(`/meters/${meterStreamId}/maxcap`);
  }

  async getTypes() {
    return await this.apiClient.get('/types');
  }

  async getDashboardData(streamIds: string[] | null = null) {
    const params = streamIds ? `?streamIds=${streamIds.join(',')}` : '';
    return await this.apiClient.get(`/dashboard${params}`);
  }

  // Helper methods
  isAuthenticated(): boolean {
    return !!this.msalInstance?.getActiveAccount();
  }

  async ensureAuthenticated(): Promise<boolean> {
    if (this.isAuthenticated()) {
      return true;
    }

    try {
      await this.msalInstance.loginRedirect(loginRequest);
      return true;
    } catch (error) {
      console.error('Login failed:', error);
      return false;
    }
  }
}

export default new AvevaApiService();
